import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingCart, User, Calendar, School, Phone, AlertCircle } from 'lucide-react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { cartAPI } from '../../services/api';

interface Program {
  id: number;
  name: string;
  monthly_price?: string;
  one_time_price?: string | null;
  effective_price?: string;
  price?: string;
  payment_type?: 'monthly' | 'one_time';
  is_one_time_payment?: boolean;
  image?: string;
}

interface ProgramLevel {
  id: number;
  name: string;
}

interface AddToCartModalProps {
  isOpen: boolean;
  onClose: () => void;
  program: Program;
  levels?: ProgramLevel[];
}

interface ChildData {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  grade: string;
  school: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relationship: string;
  authorized_pickup_persons: { name: string; relationship: string; phone: string }[];
  allergies: string;
  special_needs: string;
  preferred_hospital: string;
}

const emptyChild: ChildData = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  grade: '',
  school: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  emergency_contact_relationship: '',
  authorized_pickup_persons: [{ name: '', relationship: '', phone: '' }],
  allergies: '',
  special_needs: '',
  preferred_hospital: '',
};

const AddToCartModal: React.FC<AddToCartModalProps> = ({ isOpen, onClose, program, levels }) => {
  const queryClient = useQueryClient();
  const [selectedLevel, setSelectedLevel] = useState<number | null>(levels?.[0]?.id || null);
  const [childData, setChildData] = useState<ChildData>({ ...emptyChild });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const addToCartMutation = useMutation({
    mutationFn: (data: { program_id: number; level_id?: number | null; child_data: ChildData }) =>
      cartAPI.addItem(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries('cart');
      queryClient.invalidateQueries('cart-sidebar');
      queryClient.invalidateQueries('cart-count');
      toast.success(response.data.message || 'Added to cart!');
      onClose();
      setChildData({ ...emptyChild });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to add to cart');
    },
  });

  const handleInputChange = (field: keyof ChildData, value: string) => {
    setChildData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handlePickupChange = (index: number, field: string, value: string) => {
    setChildData((prev) => {
      const updated = [...prev.authorized_pickup_persons];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, authorized_pickup_persons: updated };
    });
  };

  const addPickupPerson = () => {
    setChildData((prev) => ({
      ...prev,
      authorized_pickup_persons: [...prev.authorized_pickup_persons, { name: '', relationship: '', phone: '' }],
    }));
  };

  const removePickupPerson = (index: number) => {
    if (childData.authorized_pickup_persons.length > 1) {
      setChildData((prev) => ({
        ...prev,
        authorized_pickup_persons: prev.authorized_pickup_persons.filter((_, i) => i !== index),
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!childData.first_name.trim()) newErrors.first_name = 'Required';
    if (!childData.last_name.trim()) newErrors.last_name = 'Required';
    if (!childData.date_of_birth) newErrors.date_of_birth = 'Required';
    if (!childData.school.trim()) newErrors.school = 'Required';
    if (!childData.emergency_contact_name.trim()) newErrors.emergency_contact_name = 'Required';
    if (!childData.emergency_contact_phone.trim()) newErrors.emergency_contact_phone = 'Required';

    const hasValidPickup = childData.authorized_pickup_persons.some(
      (p) => p.name.trim() && p.phone.trim()
    );
    if (!hasValidPickup) newErrors.pickup = 'At least one pickup person required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    addToCartMutation.mutate({
      program_id: program.id,
      level_id: selectedLevel,
      child_data: childData,
    });
  };

  const isOneTime = program.payment_type === 'one_time' || program.is_one_time_payment;
  const price = program.effective_price || program.one_time_price || program.monthly_price || program.price || '0';

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add to Cart</h2>
              <p className="text-sm text-gray-500">{program.name} - ${price}{isOneTime ? ' one time' : '/mo'}</p>
              {isOneTime && (
                <p className="text-xs text-amber-700">One-time payment. No monthly subscription charge.</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                You can check out as a guest. We will collect your contact details at checkout.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Level Selection */}
              {levels && levels.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Level</label>
                  <div className="grid grid-cols-2 gap-2">
                    {levels.map((level) => (
                      <button
                        key={level.id}
                        type="button"
                        onClick={() => setSelectedLevel(level.id)}
                        className={`p-3 rounded-lg border-2 text-center transition ${
                          selectedLevel === level.id
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {level.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Child Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User size={20} className="mr-2 text-primary-500" /> Child Information
                </h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="child-first-name" className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      id="child-first-name"
                      type="text"
                      value={childData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className={`input-field ${errors.first_name ? 'border-red-500' : ''}`}
                    />
                    {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
                  </div>
                  <div>
                    <label htmlFor="child-last-name" className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      id="child-last-name"
                      type="text"
                      value={childData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className={`input-field ${errors.last_name ? 'border-red-500' : ''}`}
                    />
                    {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
                  </div>
                  <div>
                    <label htmlFor="child-date-of-birth" className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                    <input
                      id="child-date-of-birth"
                      type="date"
                      value={childData.date_of_birth}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      className={`input-field ${errors.date_of_birth ? 'border-red-500' : ''}`}
                    />
                    {errors.date_of_birth && <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                    <input
                      type="text"
                      value={childData.grade}
                      onChange={(e) => handleInputChange('grade', e.target.value)}
                      className="input-field"
                      placeholder="e.g., 3rd"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="child-school" className="block text-sm font-medium text-gray-700 mb-1">School *</label>
                    <input
                      id="child-school"
                      type="text"
                      value={childData.school}
                      onChange={(e) => handleInputChange('school', e.target.value)}
                      className={`input-field ${errors.school ? 'border-red-500' : ''}`}
                    />
                    {errors.school && <p className="text-red-500 text-xs mt-1">{errors.school}</p>}
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Phone size={20} className="mr-2 text-primary-500" /> Emergency Contact
                </h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="emergency-contact-name" className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
                    <input
                      id="emergency-contact-name"
                      type="text"
                      value={childData.emergency_contact_name}
                      onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                      className={`input-field ${errors.emergency_contact_name ? 'border-red-500' : ''}`}
                    />
                  </div>
                  <div>
                    <label htmlFor="emergency-contact-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      id="emergency-contact-phone"
                      type="tel"
                      value={childData.emergency_contact_phone}
                      onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                      className={`input-field ${errors.emergency_contact_phone ? 'border-red-500' : ''}`}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                    <input
                      type="text"
                      value={childData.emergency_contact_relationship}
                      onChange={(e) => handleInputChange('emergency_contact_relationship', e.target.value)}
                      className="input-field"
                      placeholder="e.g., Parent, Guardian"
                    />
                  </div>
                </div>
              </div>

              {/* Authorized Pickup */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Authorized Pickup Persons *</h3>
                {errors.pickup && (
                  <p className="text-red-500 text-sm mb-2 flex items-center">
                    <AlertCircle size={14} className="mr-1" /> {errors.pickup}
                  </p>
                )}
                {childData.authorized_pickup_persons.map((person, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-2 mb-2">
                    <input
                      type="text"
                      value={person.name}
                      onChange={(e) => handlePickupChange(idx, 'name', e.target.value)}
                      placeholder="Name"
                      className="input-field"
                    />
                    <input
                      type="text"
                      value={person.relationship}
                      onChange={(e) => handlePickupChange(idx, 'relationship', e.target.value)}
                      placeholder="Relationship"
                      className="input-field"
                    />
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        value={person.phone}
                        onChange={(e) => handlePickupChange(idx, 'phone', e.target.value)}
                        placeholder="Phone"
                        className="input-field flex-grow"
                      />
                      {childData.authorized_pickup_persons.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePickupPerson(idx)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Minus size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addPickupPerson}
                  className="text-primary-600 text-sm font-medium flex items-center mt-2"
                >
                  <Plus size={16} className="mr-1" /> Add another person
                </button>
              </div>

              {/* Medical Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Medical Information (Optional)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
                    <textarea
                      value={childData.allergies}
                      onChange={(e) => handleInputChange('allergies', e.target.value)}
                      className="input-field"
                      rows={2}
                      placeholder="List any allergies..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Special Needs</label>
                    <textarea
                      value={childData.special_needs}
                      onChange={(e) => handleInputChange('special_needs', e.target.value)}
                      className="input-field"
                      rows={2}
                      placeholder="Any special needs or accommodations..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Hospital</label>
                    <input
                      type="text"
                      value={childData.preferred_hospital}
                      onChange={(e) => handleInputChange('preferred_hospital', e.target.value)}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={onClose} className="btn-outline flex-1">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addToCartMutation.isLoading}
                  className="btn-primary flex-1 justify-center"
                >
                  {addToCartMutation.isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <ShoppingCart size={18} className="mr-2" /> Add to Cart
                    </>
                  )}
                </button>
              </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddToCartModal;
